import React, { useEffect, useState } from "react";
import axios from "axios";
import config from "../../config.json";

import {
    Box,
    Grid,
    Typography,
    Card,
    CardContent,
    Container,
    Button,
} from "@mui/material";

const Home = () => {
    const [serverStats, setServerStats] = useState({
        serverOnline: false,
        onlinePlayers: 0,
    });

    useEffect(() => {
        const fetchOnlineStatus = async () => {
            try {
                const { data } = await axios.get(
                    config.apiEndPoint + "statistics"
                );

                const stats = data?.data || data || {};

                setServerStats({
                    serverOnline: !!stats.serverOnline,
                    onlinePlayers: stats.onlinePlayers ?? 0,
                });
            } catch (e) { }
        };

        fetchOnlineStatus();
    }, []);

    return (
        <Box
            sx={{
                background: "#07110D",
                color: "#F4FFF7",
                minHeight: "100vh",
                overflow: "hidden",
                fontFamily: "inherit",
            }}
        >

            {/* ========================================================= */}
            {/* HERO */}
            {/* ========================================================= */}

            <Box
                sx={{
                    minHeight: "100vh",
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",

                    background: `linear-gradient(120deg, rgba(18,77,48,.22), transparent 42%), radial-gradient(ellipse at 82% 18%, rgba(125,255,174,.10), transparent 30%), linear-gradient(155deg, #07110D 0%, #0A1C14 48%, #030906 100%)`,
                }}
            >

                {/* GOLD GLOW */}

                <Box
                    sx={{
                        position: "absolute",
                        top: "-250px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: 700,
                        height: 700,
                        borderRadius: "50%",
                        background: "rgba(57,217,138,.10)",
                        filter: "blur(140px)",
                        pointerEvents: "none",
                    }}
                />

                {/* RED GLOW */}

                <Box
                    sx={{
                        position: "absolute",
                        bottom: "-300px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: 800,
                        height: 500,
                        borderRadius: "50%",
                        background: "rgba(12,90,48,.12)",
                        filter: "blur(150px)",
                        pointerEvents: "none",
                    }}
                />

                {/* TOP DECORATIVE LINE */}

                <Box
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: 3,
                        background:
                            "linear-gradient(90deg, transparent, #46EB91, #B7FFD1, #46EB91, transparent)",
                        boxShadow: "0 0 20px rgba(57,217,138,.6)",
                    }}
                />

                <Container
                    maxWidth="lg"
                    sx={{
                        position: "relative",
                        zIndex: 2,
                        py: { xs: 8, md: 10 },
                    }}
                >

                    {/* ================================================= */}
                    {/* CENTER LOGO */}
                    {/* ================================================= */}

                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            mb: { xs: 3, md: 1 },
                        }}
                    >
                        <Box
                            component="img"
                            src="https://criticalconquer.site/files1/bg1.png"
                            alt="CriticalConquer"
                            sx={{
                                width: {
                                    xs: "90%",
                                    sm: 550,
                                    md: 700,
                                },
                                maxWidth: "100%",
                                height: "auto",

                                filter:
                                    "drop-shadow(0 0 20px rgba(57,217,138,.30)) drop-shadow(0 25px 45px rgba(0,0,0,.8))",

                                transition: "all .4s ease",

                                "&:hover": {
                                    transform: "scale(1.02)",
                                    filter:
                                        "drop-shadow(0 0 35px rgba(57,217,138,.50)) drop-shadow(0 25px 45px rgba(0,0,0,.8))",
                                },
                            }}
                        />
                    </Box>

                    {/* SMALL VERSION LABEL */}

                    <Box
                        sx={{
                            textAlign: "center",
                            mb: 3,
                        }}
                    >
                        <Typography
                            sx={{
                                color: "#46EB91",
                                fontSize: {
                                    xs: 11,
                                    md: 14,
                                },
                                fontWeight: 800,
                                letterSpacing: {
                                    xs: 3,
                                    md: 6,
                                },
                                textTransform: "uppercase",
                            }}
                        >
                            YOUR NEXT CHAPTER BEGINS IN THE REALM
                        </Typography>

                        <Typography
                            sx={{
                                color: "#9BAFA2",
                                fontSize: 12,
                                letterSpacing: 3,
                                mt: 1,
                            }}
                        >
                            CLASSIC 5165  /  BUILT FOR THE OLD-SCHOOL WARRIOR
                        </Typography>
                    </Box>

                    {/* HERO TITLE */}

                    <Box
                        sx={{
                            textAlign: "center",
                            maxWidth: 850,
                            mx: "auto",
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: {
                                    xs: 34,
                                    sm: 48,
                                    md: 66,
                                },
                                fontWeight: 900,
                                lineHeight: 1.05,
                                letterSpacing: {
                                    xs: -1,
                                    md: -2,
                                },

                                background:
                                    "linear-gradient(180deg,#F4FFF7 0%,#46EB91 45%,#167747 100%)",
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",

                                textShadow:
                                    "0 5px 25px rgba(57,217,138,.15)",
                            }}
                        >
                            THE OLD WORLD
                            <br />
                            CALLS YOU BACK
                        </Typography>

                        <Typography
                            sx={{
                                color: "#C2D2C8",
                                mt: 3,
                                mx: "auto",
                                maxWidth: 680,
                                fontSize: {
                                    xs: 15,
                                    md: 18,
                                },
                                lineHeight: 1.8,
                            }}
                        >
                            Return to a world where reputation is earned in battle. Hunt rare bosses, rally your guild, and make every duel count in a classic 5165 realm.
                        </Typography>
                    </Box>

                    {/* ================================================= */}
                    {/* BUTTONS */}
                    {/* ================================================= */}

                    <Box
                        sx={{
                            mt: 5,
                            display: "flex",
                            justifyContent: "center",
                            gap: 2,
                            flexWrap: "wrap",
                        }}
                    >

                        <Button
                            onClick={() =>
                                window.location.href =
                                "https://criticalconquer.site/user/register"
                            }
                            sx={{
                                px: {
                                    xs: 4,
                                    md: 6,
                                },
                                py: 1.8,

                                background:
                                    "linear-gradient(135deg,#46EB91 0%,#72E6A0 45%,#16824A 100%)",

                                color: "#06170D",
                                fontWeight: 900,
                                letterSpacing: 1,
                                borderRadius: 1,

                                boxShadow:
                                    "0 8px 30px rgba(70,235,145,.30)",

                                transition: ".3s",

                                "&:hover": {
                                    background:
                                        "linear-gradient(135deg,#B7FFD1,#C7FFDA,#46EB91)",
                                    transform: "translateY(-3px)",
                                    boxShadow:
                                        "0 12px 35px rgba(57,217,138,.40)",
                                },
                            }}
                        >
                            ⚔ CREATE YOUR CHARACTER
                        </Button>

                        <Button
                            onClick={() =>
                                window.location.href =
                                "https://criticalconquer.site/downloads"
                            }
                            sx={{
                                px: {
                                    xs: 4,
                                    md: 6,
                                },
                                py: 1.8,

                                color: "#46EB91",
                                fontWeight: 900,
                                letterSpacing: 1,

                                border:
                                    "1px solid rgba(57,217,138,.7)",

                                borderRadius: 1,

                                background:
                                    "rgba(57,217,138,.03)",

                                transition: ".3s",

                                "&:hover": {
                                    background:
                                        "rgba(57,217,138,.10)",
                                    borderColor: "#B7FFD1",
                                    transform: "translateY(-3px)",
                                },
                            }}
                        >
                            GET THE GAME CLIENT
                        </Button>

                    </Box>

                    {/* ================================================= */}
                    {/* WORLD STATUS */}
                    {/* ================================================= */}

                    <Card
                        sx={{
                            maxWidth: 850,
                            mx: "auto",
                            mt: 6,

                            background:
                                "linear-gradient(135deg,rgba(12,32,20,.95),rgba(5,18,10,.95))",

                            border:
                                "1px solid rgba(57,217,138,.28)",

                            borderRadius: 1,

                            boxShadow:
                                "0 20px 60px rgba(0,0,0,.55)",

                            position: "relative",
                            overflow: "hidden",

                            "&:before": {
                                content: '""',
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: 2,
                                background:
                                    "linear-gradient(90deg,transparent,#46EB91,#B7FFD1,#46EB91,transparent)",
                            },
                        }}
                    >

                        <CardContent
                            sx={{
                                p: {
                                    xs: 3,
                                    md: 4,
                                },
                            }}
                        >

                            <Grid
                                container
                                spacing={3}
                                alignItems="center"
                            >

                                {/* PLAYERS */}

                                <Grid item xs={6} md={4}>
                                    <Typography
                                        sx={{
                                            color: "#70B98B",
                                            fontSize: 11,
                                            fontWeight: 800,
                                            letterSpacing: 2,
                                        }}
                                    >
                                        WARRIORS IN THE REALM
                                    </Typography>

                                    <Typography
                                        sx={{
                                            mt: .5,
                                            fontSize: {
                                                xs: 30,
                                                md: 38,
                                            },
                                            fontWeight: 900,
                                            color: "#f3d47a",
                                        }}
                                    >
                                        {serverStats.onlinePlayers}
                                    </Typography>
                                </Grid>

                                {/* STATUS */}

                                <Grid item xs={6} md={4}>
                                    <Typography
                                        sx={{
                                            color: "#70B98B",
                                            fontSize: 11,
                                            fontWeight: 800,
                                            letterSpacing: 2,
                                        }}
                                    >
                                        WORLD STATUS
                                    </Typography>

                                    <Typography
                                        sx={{
                                            mt: .5,
                                            fontSize: {
                                                xs: 22,
                                                md: 28,
                                            },
                                            fontWeight: 900,
                                            color:
                                                serverStats.serverOnline
                                                    ? "#55c878"
                                                    : "#F06B6B",
                                        }}
                                    >
                                        {serverStats.serverOnline
                                            ? "● ONLINE"
                                            : "● OFFLINE"}
                                    </Typography>
                                </Grid>

                                {/* VERSION */}

                                <Grid item xs={12} md={4}>
                                    <Typography
                                        sx={{
                                            color: "#70B98B",
                                            fontSize: 11,
                                            fontWeight: 800,
                                            letterSpacing: 2,
                                        }}
                                    >
                                        SERVER EDITION
                                    </Typography>

                                    <Typography
                                        sx={{
                                            mt: .5,
                                            fontSize: 22,
                                            fontWeight: 900,
                                            color: "#E2F5E9",
                                        }}
                                    >
                                        VERSION 5165
                                    </Typography>
                                </Grid>

                            </Grid>

                            {/* STATUS BAR */}

                            <Box
                                sx={{
                                    mt: 3,
                                    height: 3,
                                    borderRadius: 10,
                                    background:
                                        "rgba(180,255,205,.08)",
                                    overflow: "hidden",
                                }}
                            >
                                <Box
                                    sx={{
                                        width: "100%",
                                        height: "100%",
                                        background:
                                            "linear-gradient(90deg,#167747,#46EB91,#B7FFD1,#46EB91,#167747)",
                                    }}
                                />
                            </Box>

                            <Typography
                                sx={{
                                    mt: 2,
                                    color: "#9BAFA2",
                                    textAlign: "center",
                                    fontSize: 12,
                                }}
                            >
                                Classic 5165 experience • Guild rivalries • Boss hunts • Community-driven battles
                            </Typography>

                        </CardContent>
                    </Card>

                </Container>
            </Box>


            {/* ========================================================= */}
            {/* CLASSIC FEATURES */}
            {/* ========================================================= */}

            <Box
                sx={{
                    position: "relative",
                    py: {
                        xs: 9,
                        md: 13,
                    },

                    background:
                        "linear-gradient(180deg,#07110D 0%,#0D2418 50%,#07110D 100%)",

                    borderTop:
                        "1px solid rgba(57,217,138,.12)",
                }}
            >

                {/* BACKGROUND GLOW */}

                <Box
                    sx={{
                        position: "absolute",
                        top: -200,
                        left: -200,
                        width: 500,
                        height: 500,
                        borderRadius: "50%",
                        background:
                            "rgba(57,217,138,.06)",
                        filter: "blur(130px)",
                    }}
                />

                <Box
                    sx={{
                        position: "absolute",
                        bottom: -200,
                        right: -200,
                        width: 500,
                        height: 500,
                        borderRadius: "50%",
                        background:
                            "rgba(12,90,48,.07)",
                        filter: "blur(130px)",
                    }}
                />

                <Container
                    maxWidth="lg"
                    sx={{
                        position: "relative",
                        zIndex: 2,
                    }}
                >

                    {/* SECTION TITLE */}

                    <Box
                        sx={{
                            textAlign: "center",
                            mb: 8,
                        }}
                    >

                        <Typography
                            sx={{
                                color: "#70B98B",
                                fontSize: 12,
                                fontWeight: 900,
                                letterSpacing: 4,
                                mb: 2,
                            }}
                        >
                            THE RULES THAT SHAPE OUR WORLD
                        </Typography>

                        <Typography
                            sx={{
                                fontSize: {
                                    xs: 36,
                                    md: 58,
                                },
                                fontWeight: 900,
                                lineHeight: 1.1,
                            }}
                        >
                            A REALM BUILT AROUND
                            <span
                                style={{
                                    color: "#46EB91",
                                }}
                            >
                                {" "}CLASSIC CONQUER
                            </span>
                        </Typography>

                        <Typography
                            sx={{
                                color: "#9BAFA2",
                                maxWidth: 650,
                                mx: "auto",
                                mt: 3,
                                lineHeight: 1.8,
                            }}
                        >
                            A deliberate old-school setup: familiar progression, meaningful rivalries, and room for skill to decide the outcome.
                        </Typography>

                    </Box>


                    <Grid
                        container
                        spacing={4}
                    >

                        {/* ================================================= */}
                        {/* THE FOUNDATION */}
                        {/* ================================================= */}

                        <Grid item xs={12} md={6}>

                            <Card
                                sx={{
                                    height: "100%",
                                    background:
                                        "linear-gradient(145deg,#102719,#08170D)",

                                    border:
                                        "1px solid rgba(70,235,145,.30)",

                                    borderRadius: 1,

                                    boxShadow:
                                        "0 25px 60px rgba(0,0,0,.4)",

                                    position: "relative",
                                    overflow: "hidden",
                                }}
                            >

                                <CardContent
                                    sx={{
                                        p: {
                                            xs: 4,
                                            md: 5,
                                        },
                                    }}
                                >

                                    <Typography
                                        sx={{
                                            color: "#46EB91",
                                            fontWeight: 900,
                                            fontSize: 12,
                                            letterSpacing: 3,
                                            mb: 3,
                                        }}
                                    >
                                        THE FOUNDATION
                                    </Typography>

                                    <Typography
                                        sx={{
                                            fontSize: {
                                                xs: 34,
                                                md: 46,
                                            },
                                            fontWeight: 900,
                                            lineHeight: 1.1,
                                            mb: 3,
                                        }}
                                    >
                                        No shortcuts.
                                        <br />
                                        No easy crowns.
                                        <br />
                                        <span
                                            style={{
                                                color: "#46EB91",
                                            }}
                                        >
                                            Earn your name.
                                        </span>
                                    </Typography>

                                    <Typography
                                        sx={{
                                            color: "#B1C5B7",
                                            lineHeight: 1.9,
                                            mb: 4,
                                        }}
                                    >
                                        Progress through a classic ruleset where preparation matters. Build your gear, learn your class, and bring your guild when the realm calls.
                                    </Typography>


                                    <Grid
                                        container
                                        spacing={1.5}
                                    >

                                        {[
                                            "Level 130",
                                            "Max +9",
                                            "Bless -5",
                                            "1st Reborn",
                                            "4 Main Classes",
                                            "No Ninja / Monk",
                                            "Classic Interface",
                                            "Singapore Hosted",
                                        ].map((feature) => (

                                            <Grid
                                                item
                                                xs={6}
                                                key={feature}
                                            >

                                                <Box
                                                    sx={{
                                                        p: 1.8,

                                                        background:
                                                            "linear-gradient(135deg,rgba(57,217,138,.09),rgba(57,217,138,.025))",

                                                        border:
                                                            "1px solid rgba(70,235,145,.18)",

                                                        borderRadius: 1,

                                                        textAlign: "center",

                                                        transition: ".3s",

                                                        "&:hover": {
                                                            borderColor:
                                                                "#46EB91",

                                                            background:
                                                                "rgba(57,217,138,.12)",

                                                            transform:
                                                                "translateY(-3px)",
                                                        },
                                                    }}
                                                >

                                                    <Typography
                                                        sx={{
                                                            fontSize: 13,
                                                            fontWeight: 700,
                                                            color: "#E2F5E9",
                                                        }}
                                                    >
                                                        {feature}
                                                    </Typography>

                                                </Box>

                                            </Grid>

                                        ))}

                                    </Grid>

                                </CardContent>

                            </Card>

                        </Grid>


                        {/* ================================================= */}
                        {/* CLASSIC CONTENT */}
                        {/* ================================================= */}

                        <Grid item xs={12} md={6}>

                            <Card
                                sx={{
                                    height: "100%",

                                    background:
                                        "linear-gradient(145deg,#102719,#08170D)",

                                    border:
                                        "1px solid rgba(70,235,145,.30)",

                                    borderRadius: 1,

                                    boxShadow:
                                        "0 25px 60px rgba(0,0,0,.4)",
                                }}
                            >

                                <CardContent
                                    sx={{
                                        p: {
                                            xs: 4,
                                            md: 5,
                                        },
                                    }}
                                >

                                    <Typography
                                        sx={{
                                            color: "#46EB91",
                                            fontWeight: 900,
                                            fontSize: 12,
                                            letterSpacing: 3,
                                            mb: 3,
                                        }}
                                    >
                                        ACTIVITIES THAT KEEP THE WORLD MOVING
                                    </Typography>

                                    <Typography
                                        sx={{
                                            fontSize: {
                                                xs: 34,
                                                md: 46,
                                            },
                                            fontWeight: 900,
                                            lineHeight: 1.15,
                                            mb: 4,
                                        }}
                                    >
                                        Find Your Next
                                        <br />
                                        <span
                                            style={{
                                                color: "#46EB91",
                                            }}
                                        >
                                            Challenge.
                                        </span>
                                    </Typography>


                                    <Grid
                                        container
                                        spacing={1.5}
                                    >

                                        {[
                                            "Blue Mouse Quest",
                                            "Terato Dragon",
                                            "MetsZone",
                                            "DBDevil Boss",
                                            "CleanWater Lord",
                                            "High FPS",
                                            "Low Ping",
                                            "Active Community",
                                        ].map((feature) => (

                                            <Grid
                                                item
                                                xs={6}
                                                key={feature}
                                            >

                                                <Box
                                                    sx={{
                                                        p: 1.8,

                                                        background:
                                                            "linear-gradient(135deg,rgba(57,217,138,.09),rgba(57,217,138,.025))",

                                                        border:
                                                            "1px solid rgba(70,235,145,.18)",

                                                        borderRadius: 1,

                                                        textAlign: "center",

                                                        transition: ".3s",

                                                        "&:hover": {
                                                            borderColor:
                                                                "#46EB91",

                                                            transform:
                                                                "translateY(-3px)",
                                                        },
                                                    }}
                                                >

                                                    <Typography
                                                        sx={{
                                                            fontSize: 13,
                                                            fontWeight: 700,
                                                            color: "#E2F5E9",
                                                        }}
                                                    >
                                                        {feature}
                                                    </Typography>

                                                </Box>

                                            </Grid>

                                        ))}

                                    </Grid>


                                    {/* DESCRIPTION BOX */}

                                    <Box
                                        sx={{
                                            mt: 4,
                                            p: 3,

                                            borderRadius: 1,

                                            background:
                                                "rgba(57,217,138,.045)",

                                            border:
                                                "1px solid rgba(57,217,138,.12)",
                                        }}
                                    >

                                        <Typography
                                            sx={{
                                                color: "#B1C5B7",
                                                lineHeight: 1.9,
                                                fontSize: 14,
                                            }}
                                        >
                                            From boss encounters to guild clashes, Critical Conquer keeps the focus on classic character building and player-versus-player competition.
                                        </Typography>

                                        <Typography
                                            sx={{
                                                color: "#46EB91",
                                                mt: 2,
                                                fontWeight: 800,
                                                fontSize: 13,
                                            }}
                                        >
                                            BOSS HUNTS • GUILD WARS • TOURNAMENTS
                                        </Typography>

                                    </Box>

                                </CardContent>

                            </Card>

                        </Grid>

                    </Grid>

                </Container>
            </Box>


            {/* ========================================================= */}
            {/* THE FIELD GUIDE */}
            {/* ========================================================= */}

            <Box
                sx={{
                    py: { xs: 8, md: 10 },
                    background: "linear-gradient(180deg,#07110D 0%,#0B2015 100%)",
                    borderTop: "1px solid rgba(70,235,145,.16)",
                    borderBottom: "1px solid rgba(70,235,145,.16)",
                }}
            >
                <Container maxWidth="lg">
                    <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, alignItems: { md: "end" }, justifyContent: "space-between", gap: 3, mb: 5 }}>
                        <Box>
                            <Typography sx={{ color: "#46EB91", fontSize: 11, fontWeight: 900, letterSpacing: 4, mb: 1.5 }}>
                                FIELD GUIDE / 01
                            </Typography>
                            <Typography sx={{ color: "#F0FFF5", fontSize: { xs: 30, md: 44 }, fontWeight: 900, lineHeight: 1.1 }}>
                                THREE WAYS TO MAKE A NAME
                            </Typography>
                        </Box>
                        <Typography sx={{ color: "#9FB9A8", maxWidth: 420, lineHeight: 1.8 }}>
                            There is more than one road to renown. Prepare carefully, explore boldly, and bring trusted allies into every fight.
                        </Typography>
                    </Box>
                    <Grid container spacing={2.5}>
                        {[
                            { number: "01", title: "HUNT THE UNKNOWN", text: "Track down challenging bosses and search for rewards that make every expedition worth the risk." },
                            { number: "02", title: "STAND WITH YOUR GUILD", text: "Coordinate with your allies, defend your reputation, and turn guild rivalries into server history." },
                            { number: "03", title: "MASTER YOUR BUILD", text: "Tune your equipment, learn your class, and prove that timing and preparation beat empty boasts." },
                        ].map((item) => (
                            <Grid item xs={12} md={4} key={item.number}>
                                <Card sx={{ height: "100%", background: "linear-gradient(145deg,rgba(17,48,31,.92),rgba(6,19,12,.96))", border: "1px solid rgba(70,235,145,.20)", borderRadius: 1, boxShadow: "0 18px 38px rgba(0,0,0,.24)", transition: ".25s", "&:hover": { transform: "translateY(-5px)", borderColor: "#46EB91", boxShadow: "0 22px 45px rgba(0,0,0,.36)" } }}>
                                    <CardContent sx={{ p: { xs: 3, md: 3.5 } }}>
                                        <Typography sx={{ color: "#46EB91", fontSize: 12, fontWeight: 900, letterSpacing: 2, mb: 2 }}>/{item.number}</Typography>
                                        <Typography sx={{ color: "#F0FFF5", fontSize: 18, fontWeight: 900, mb: 1.5 }}>{item.title}</Typography>
                                        <Typography sx={{ color: "#A9C0B1", fontSize: 14, lineHeight: 1.85 }}>{item.text}</Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* ========================================================= */}
            {/* BOTTOM CTA */}
            {/* ========================================================= */}

            <Box
                sx={{
                    py: {
                        xs: 9,
                        md: 12,
                    },

                    textAlign: "center",

                    background:
                        "radial-gradient(circle at center,rgba(57,217,138,.16),transparent 55%),#07110D",

                    borderTop:
                        "1px solid rgba(57,217,138,.12)",
                }}
            >

                <Container maxWidth="md">

                    <Typography
                        sx={{
                            color: "#70B98B",
                            fontSize: 12,
                            fontWeight: 900,
                            letterSpacing: 4,
                            mb: 2,
                        }}
                    >
                        YOUR STORY IS STILL UNWRITTEN
                    </Typography>

                    <Typography
                        sx={{
                            fontSize: {
                                xs: 36,
                                md: 58,
                            },
                            fontWeight: 900,
                            lineHeight: 1.1,
                        }}
                    >
                        MAKE YOUR MARK IN
                        <br />
                        <span
                            style={{
                                color: "#46EB91",
                            }}
                        >
                            Critical Conquer
                        </span>
                    </Typography>

                    <Typography
                        sx={{
                            color: "#9BAFA2",
                            mt: 3,
                            mb: 4,
                        }}
                    >
                        Build your character. Join your guild.
                        <br />
                        Become part of the legend.
                    </Typography>

                    <Button
                        onClick={() =>
                            window.location.href =
                            "https://criticalconquer.site/user/register"
                        }
                        sx={{
                            px: 6,
                            py: 1.8,

                            background:
                                "linear-gradient(135deg,#46EB91,#B7FFD1,#0D6335)",

                            color: "#06170D",
                            fontWeight: 900,
                            letterSpacing: 1,

                            borderRadius: 1,

                            boxShadow:
                                "0 10px 40px rgba(70,235,145,.30)",

                            "&:hover": {
                                background:
                                    "linear-gradient(135deg,#B7FFD1,#C7FFDA,#46EB91)",
                                transform: "translateY(-3px)",
                            },
                        }}
                    >
                        ENTER THE REALM
                    </Button>

                </Container>

            </Box>

        </Box>
    );
};

export default Home;